import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFamily } from '../context/FamilyContext';
import { usePermissions } from '../hooks/usePermissions';
import { subscribeToCustodyOverrides, respondToOverride, deleteCustodyOverride } from '@coparent/shared/src/services/custodyService';
import { format } from 'date-fns';
import { toJsDate } from '@coparent/shared/src/utils/dateUtils';
import {
  HiSwitchHorizontal,
  HiCheck,
  HiX,
  HiArrowLeft,
  HiCalendar,
  HiClock,
  HiTrash,
  HiOutlinePaperAirplane
} from 'react-icons/hi';
import toast from 'react-hot-toast';
import NotificationBell from '../components/notifications/NotificationBell';
import DiscussButton from '../components/common/DiscussButton';

const ymdToDate = (ymd) =>
  typeof ymd === 'string' ? new Date(ymd + 'T00:00:00') : toJsDate(ymd);

const REASON_LABELS = {
  swap: 'Day swap',
  vacation: 'Vacation',
  work: 'Work conflict',
  other: 'Other',
};

const STATUS_STYLES = {
  confirmed: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', label: 'Approved' },
  rejected: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Rejected' },
  proposed: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', label: 'Pending' },
};

const SwapCard = ({ override, family, currentUser, familyId, isPending, coParentMissing }) => {
  const [responding, setResponding] = useState(false);
  const { canDelete } = usePermissions();

  // Resolve potentially stale placeholder IDs to the real parent.
  // proposedBy is always a real UID (only authenticated users create swaps),
  // so if any other ID is stale, the missing parent is the other one.
  const resolveId = (id) => {
    if (!id || family?.parentProfiles?.[id]) return id;
    if (family?.parentIds?.length === 2) {
      return family.parentIds.find(pid => pid !== override.proposedBy) || id;
    }
    return id;
  };

  const proposer = family?.parentProfiles?.[resolveId(override.proposedBy)];
  const responder = override.respondedBy ? family?.parentProfiles?.[resolveId(override.respondedBy)] : null;
  const newParent = family?.parentProfiles?.[resolveId(override.newParentId)];
  const originalParent = family?.parentProfiles?.[resolveId(override.originalParentId)];
  const isForMe = override.proposedBy !== currentUser.uid;
  const canRespond = isForMe || coParentMissing;
  const status = STATUS_STYLES[override.status] || STATUS_STYLES.proposed;

  const isTrip = override.type === 'trip';
  const overrideDate = override.date ? ymdToDate(override.date) : null;
  const tripStart = override.startDate ? ymdToDate(override.startDate) : overrideDate;
  const tripEnd = override.endDate ? ymdToDate(override.endDate) : overrideDate;
  const dateLabel = isTrip
    ? (tripStart && tripEnd ? `${format(tripStart, 'MMM d')} – ${format(tripEnd, 'MMM d, yyyy')}` : 'Unknown dates')
    : (overrideDate ? format(overrideDate, 'EEEE, MMMM d, yyyy') : 'Unknown date');
  const noun = isTrip ? 'Trip' : 'Swap';

  const handleRespond = async (newStatus) => {
    setResponding(true);
    try {
      await respondToOverride(familyId, override.id, newStatus, currentUser.uid);
      toast.success(newStatus === 'confirmed' ? `${noun} approved!` : `${noun} rejected`);
    } catch (error) {
      toast.error('Failed to respond');
    }
    setResponding(false);
  };

  const handleDelete = async () => {
    setResponding(true);
    try {
      await deleteCustodyOverride(familyId, override.id);
      toast.success(isTrip ? (isPending ? 'Trip withdrawn' : 'Trip cancelled') : 'Swap request withdrawn');
    } catch (error) {
      toast.error('Failed to delete');
    }
    setResponding(false);
  };

  return (
    <div className={`card p-5 border-l-4 ${status.border}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {/* Trip title */}
          {isTrip && override.title && (
            <p className="font-bold text-sm text-ink-900 mb-1">{override.title}</p>
          )}

          {/* Date / range and status */}
          <div className="flex items-center gap-2.5 mb-2">
            <div className="flex items-center gap-1.5 text-ink-800">
              {isTrip
                ? <HiOutlinePaperAirplane className="text-sm text-parent1-500 rotate-45" />
                : <HiCalendar className="text-sm text-ink-400" />}
              <span className="font-bold text-sm">{dateLabel}</span>
            </div>
          </div>

          {/* Details: trip assigns the whole range to one parent; swap is a from→to */}
          {isTrip ? (
            <div className="flex items-center gap-2 text-sm text-ink-500 mb-1.5">
              <span className="text-xs text-ink-400">Kids with</span>
              <div
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: newParent?.color || '#d6d3d1' }}
              />
              <span>{newParent?.displayName || 'Unknown'}</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-ink-500 mb-1.5">
              <div
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: originalParent?.color || '#d6d3d1' }}
              />
              <span>{originalParent?.displayName || 'Unknown'}</span>
              <HiSwitchHorizontal className="text-ink-300 text-xs flex-shrink-0" />
              <div
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: newParent?.color || '#d6d3d1' }}
              />
              <span>{newParent?.displayName || 'Unknown'}</span>
            </div>
          )}

          {/* Reason and note. Trips have no swap-category reason, so show only the note. */}
          {isTrip ? (
            override.note && (
              <div className="flex items-center gap-2 text-xs text-ink-400 mt-2">
                <span className="italic">"{override.note}"</span>
              </div>
            )
          ) : (
            <div className="flex items-center gap-2 text-xs text-ink-400 mt-2">
              <span className="font-semibold uppercase tracking-wide">{REASON_LABELS[override.reason] || override.reason}</span>
              {override.note && (
                <>
                  <span className="text-ink-200">·</span>
                  <span className="italic">"{override.note}"</span>
                </>
              )}
            </div>
          )}

          {/* Who proposed */}
          <p className="text-xs text-ink-300 mt-2">
            Proposed by {proposer?.displayName || 'Unknown'}
            {override.createdAt && (
              <> · {format(toJsDate(override.createdAt), 'MMM d, h:mm a')}</>
            )}
          </p>

          {/* Who responded (for history) */}
          {responder && override.status !== 'proposed' && (
            <p className="text-xs text-ink-300 mt-0.5">
              {status.label} by {responder.displayName}
            </p>
          )}
        </div>

        {/* Status badge */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <DiscussButton
            type="swap"
            id={override.id}
            label={`${noun} · ${dateLabel}`}
          />
          <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold uppercase tracking-wide ${status.bg} ${status.text}`}>
            {status.label}
          </span>
        </div>
      </div>

      {/* Action buttons for pending swaps */}
      {isPending && canRespond && (
        <div className="flex gap-2 mt-3 pt-3 border-t border-surface-100">
          <button
            onClick={() => handleRespond('confirmed')}
            disabled={responding}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-600 text-white hover:bg-green-700 transition-colors"
          >
            <HiCheck className="text-sm" />
            Approve
          </button>
          <button
            onClick={() => handleRespond('rejected')}
            disabled={responding}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
          >
            <HiX className="text-sm" />
            Reject
          </button>
        </div>
      )}

      {/* Withdraw button for pending swaps I proposed (only when co-parent exists) */}
      {isPending && !canRespond && canDelete && (
        <div className="mt-4 pt-4 border-t border-surface-100">
          <button
            onClick={handleDelete}
            disabled={responding}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-ink-400 hover:text-red-600 hover:bg-red-50 transition-colors"
          >
            <HiTrash className="text-sm" />
            {isTrip ? 'Withdraw trip' : 'Withdraw request'}
          </button>
        </div>
      )}

      {/* Cancel button for a confirmed trip — reverts custody for the whole range */}
      {!isPending && isTrip && override.status === 'confirmed' && canDelete && (
        <div className="mt-4 pt-4 border-t border-surface-100">
          <button
            onClick={handleDelete}
            disabled={responding}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-ink-400 hover:text-red-600 hover:bg-red-50 transition-colors"
          >
            <HiTrash className="text-sm" />
            Cancel trip
          </button>
        </div>
      )}
    </div>
  );
};

const SwapsPage = () => {
  const { currentUser } = useAuth();
  const { family, familyId } = useFamily();
  const [overrides, setOverrides] = useState([]);

  useEffect(() => {
    if (!familyId) return;
    return subscribeToCustodyOverrides(familyId, setOverrides);
  }, [familyId]);

  const { pending, history } = useMemo(() => {
    const pending = overrides
      .filter(o => o.status === 'proposed')
      .sort((a, b) => {
        // Show ones awaiting my response first
        const aForMe = a.proposedBy !== currentUser.uid ? 0 : 1;
        const bForMe = b.proposedBy !== currentUser.uid ? 0 : 1;
        if (aForMe !== bForMe) return aForMe - bForMe;
        return 0;
      });

    const history = overrides
      .filter(o => o.status === 'confirmed' || o.status === 'rejected')
      .sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime;
      });

    return { pending, history };
  }, [overrides, currentUser.uid]);

  // Co-parent is "missing" if there's only 1 parent or the other parent is a placeholder
  const coParentMissing = !family?.parentIds || family.parentIds.length === 1 ||
    family.parentIds.some(pid => pid !== currentUser.uid && family.parentProfiles?.[pid]?.placeholder);
  const pendingForMe = pending.filter(o => coParentMissing || o.proposedBy !== currentUser.uid).length;

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between page-header">
        <div>
          <h1 className="page-title">Swaps &amp; Trips</h1>
          <p className="page-subtitle">
            {pendingForMe > 0
              ? `${pendingForMe} request${pendingForMe > 1 ? 's' : ''} waiting for your response`
              : 'Manage custody day swaps and trip blocks'
            }
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/calendar"
            className="btn-secondary flex items-center gap-2 text-sm"
          >
            <HiArrowLeft className="text-sm" />
            Calendar
          </Link>
          <NotificationBell />
        </div>
      </div>

      {/* Pending Swaps */}
      <section className="mb-8">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center">
            <HiClock className="text-amber-500 text-sm" />
          </div>
          <h2 className="font-bold text-ink-900 text-[0.9375rem]">Pending Requests</h2>
          {pending.length > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">
              {pending.length}
            </span>
          )}
        </div>

        {pending.length === 0 ? (
          <div className="card p-8 text-center">
            <HiSwitchHorizontal className="text-3xl text-ink-200 mx-auto mb-2" />
            <p className="text-sm text-ink-400 font-medium">No pending requests</p>
            <p className="text-xs text-ink-300 mt-1">Click a day on the calendar to propose a swap, or use “Plan a trip” for a date range</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pending.map(override => (
              <SwapCard
                key={override.id}
                override={override}
                family={family}
                currentUser={currentUser}
                familyId={familyId}
                isPending
                coParentMissing={coParentMissing}
              />
            ))}
          </div>
        )}
      </section>

      {/* Swap History */}
      <section>
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-7 h-7 rounded-lg bg-surface-100 flex items-center justify-center">
            <HiSwitchHorizontal className="text-ink-400 text-sm" />
          </div>
          <h2 className="font-bold text-ink-900 text-[0.9375rem]">History</h2>
        </div>

        {history.length === 0 ? (
          <div className="card p-8 text-center">
            <p className="text-sm text-ink-400 font-medium">No history yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {history.map(override => (
              <SwapCard
                key={override.id}
                override={override}
                family={family}
                currentUser={currentUser}
                familyId={familyId}
                isPending={false}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default SwapsPage;
